import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';
import Divider from '@material-ui/core/Divider';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import { withStyles } from '@material-ui/core/styles';

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithCredential,
  signInWithPopup,
  fetchSignInMethodsForEmail,
  getMultiFactorResolver,
  GoogleAuthProvider,
  OAuthProvider,
  linkWithCredential,
  EmailAuthProvider,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebaseConfig';

import MFAChallengeDialog from '../Components/MFA/MFAChallengeDialog';

import { GoogleLogin } from '@react-oauth/google';

import green from '@material-ui/core/colors/green';

import API, {
  GetUserFirebaseSigninPathForApi,
  GetUserMigrationCheckPathForApi,
  GetUserEstablishPasswordPathForApi,
  GetUserEmailLoginOTPSendPathForApi,
  GetUserMFAVerifyTOTPPathForApi,
  GetUserMFAResetRecoveryPathForApi,
} from '../Util/api';

import MultiUseDialog from '../Components/MultiUseDialog';
import ProgressIndicator from '../Components/ProgressIndicator';
import { ValidateEmail } from '../Util/Regex';
import { IsMobile } from '../Util/MobileDetector';
import {
  GetBrowserFingerprint,
} from '../Util/SignIn';
import {
  clearMarketingAttribution,
  clearSessionStoragePreservingAttribution,
  readMarketingAttribution,
} from '../Util/MarketingAttribution';
import { firebaseAuthMethod, pushMarketingEvent } from '../Util/MarketingEvents';
import { PRIVACY_CHOICE_STORAGE_KEY, PRIVACY_POLICY_URL, resetPrivacyChoice } from '../Util/PrivacyConsent';
import {
  GetSignInPath,
} from '../Util/PathHelper';
import {
  HandleSignIn,
  QuickBooksButtonClasses,
} from '../Util/QuickBooks';
import { withCookies } from 'react-cookie';

const linksColor = "#888";
const styles = theme => ({
  root: {
    width:"100%",
    height:"100%",
    backgroundColor:"#222",
    backgroundRepeat:"no-repeat",
    backgroundAttachment:"fixed",
    backgroundSize:"cover",
    overflowY:"auto",
    display:"flex",
    justifyContent:"center",
  },
  container: {
    marginTop:24,
    display:"flex",
    flexDirection:"column",
    alignItems:"center",
  },
  paper: {
    width:(IsMobile()) ? 320 : 400,
    padding: theme.spacing(4),
    paddingBottom:theme.spacing(1),
    borderRadius:theme.spacing(2),
  },
  logo: {
    width:"75%",
    marginBottom:theme.spacing(1),
  },
  gridControls: {
    textAlign:"center",
    marginTop:theme.spacing(2),
    marginBottom:theme.spacing(2),
  },
  button: {
    textTransform:"none",
  },
  or: {
    marginTop:theme.spacing(1),
    marginBottom:theme.spacing(1),
  },
  gridLinks: {
    color:linksColor,
    marginTop:theme.spacing(3),
    justifyContent:"center",
    alignItems:"center",
    "& .MuiTypography-root": {
      fontSize:16,
    },
  },
  divider:{
    backgroundColor:"#aaa",
  },
  ...QuickBooksButtonClasses,
});

const _browserFingerprint = GetBrowserFingerprint();

// Firebase Apple provider
const appleProvider = new OAuthProvider('apple.com');

const Login = ({ classes, theme, match, location, cookies }) => {
  const SignInRoute = (match && match.params && match.params.route) || "new";

  // Core state
  const [redirectToReferrer, setRedirectToReferrer] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Dialog states
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [generalDialogDetails, setGeneralDialogDetails] = useState({ Open: false });

  // Migration flow
  const [migrationOTP, setMigrationOTP] = useState('');
  const [migrationPassword, setMigrationPassword] = useState('');
  const [migrationPasswordConfirm, setMigrationPasswordConfirm] = useState('');

  // Loading
  const [showProgressIndicator, setShowProgressIndicator] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);

  // Account linking
  const [accountLinkingContext, setAccountLinkingContext] = useState(null);
  const [pendingCredential, setPendingCredential] = useState(null);

  // MFA challenge (Firebase resolver-based, for native OAuth sign-ins)
  const [showMFADialog, setShowMFADialog] = useState(false);
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaEmail, setMfaEmail] = useState('');

  // Server-side TOTP verification (for custom token sign-ins like Intuit)
  const [showTOTPDialog, setShowTOTPDialog] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [totpError, setTotpError] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [pendingFirebaseUser, setPendingFirebaseUser] = useState(null);
  const [totpShowRecovery, setTotpShowRecovery] = useState(false);
  const [totpRecoveryCode, setTotpRecoveryCode] = useState('');
  const [totpRecoveryLoading, setTotpRecoveryLoading] = useState(false);
  const [totpRecoverySuccess, setTotpRecoverySuccess] = useState(false);

  // Google button width
  const [googleButtonWidth, setGoogleButtonWidth] = useState(null);
  const buttonGridRef = useRef(null);
  const ButtonGridSpacing = 2;

  const nativeAppMode = cookies.get("native_app_mode");
  const isLogin = (SignInRoute !== "new");

  const { from } = (location && location.state)
    || { from: { pathname: '/' } };

  // Utility functions
  const showGeneralDialog = useCallback((title, bodyText, closeCallback) => {
    setGeneralDialogDetails({
      Open: true,
      Title: title,
      BodyText: bodyText,
      CloseCallback: () => {
        setGeneralDialogDetails({ Open: false });
        if (closeCallback) closeCallback();
      },
    });
  }, []);

  const handleApiError = useCallback(err => {
    setShowProgressIndicator(false);

    if (typeof err === "string") {
      showGeneralDialog("Sorry, something went wrong...", err);
      return;
    }

    if (typeof err.response === "undefined") {
      err = {
        response: {
          status: 500,
          data: {
            message: err.toString(),
          },
        },
      };
    }

    const errorMessage = (err.response.data.message)
      ? err.response.data.message
      : "Please try again.";

    showGeneralDialog("Sorry, something went wrong...", errorMessage, err.closeCallback);
  }, [showGeneralDialog]);

  // Map provider IDs to friendly names
  const providerDisplayName = (providerId) => {
    switch (providerId) {
      case 'google.com': return 'Google';
      case 'apple.com': return 'Apple';
      case 'password': return 'email and password';
      default: return providerId;
    }
  };

  // Handle account-exists-with-different-credential error
  const handleAccountExistsError = useCallback(async (error, attemptedProvider) => {
    const errorEmail = error.customData?.email;
    const credential = OAuthProvider.credentialFromError(error);

    if (!errorEmail) {
      showGeneralDialog("Account Linking Required",
        "An account already exists with this email using a different sign-in method. Please sign in with your original method first.");
      return;
    }

    setPendingCredential(credential);

    let providerName = null;
    try {
      const methods = await fetchSignInMethodsForEmail(auth, errorEmail);
      if (methods.length > 0) {
        providerName = providerDisplayName(methods[0]);
      }
    } catch (e) {
      // Ignore - email enumeration protection may block this
    }

    const allProviders = ['Google', 'Apple', 'email and password'];
    const otherProviders = allProviders.filter(p => p !== attemptedProvider);

    const message = providerName
      ? `An account already exists for ${errorEmail} using ${providerName}. Please sign in with ${providerName} first, and your accounts will be automatically linked.`
      : `An account already exists for ${errorEmail} using a different sign-in method. Please sign in using one of your other methods (${otherProviders.join(', ')}), and your accounts will be automatically linked.`;

    showGeneralDialog("Account Linking", message);
  }, [showGeneralDialog]);

  // Mask email for display
  const maskEmail = (email) => {
    if (!email || !email.includes('@')) return email;
    const [local, domain] = email.split('@');
    if (local.length <= 1) return `${local}***@${domain}`;
    return `${local[0]}***@${domain}`;
  };

  // Google button width - resize handler
  useEffect(() => {
    const handleResize = () => {
      if (buttonGridRef.current && buttonGridRef.current.clientWidth) {
        setGoogleButtonWidth(
          buttonGridRef.current.clientWidth - (theme.spacing(ButtonGridSpacing) || 0)
        );
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    // Clear per-session context without destroying the acquisition handoff or the visitor's cookie choice.
    clearSessionStoragePreservingAttribution(undefined, [PRIVACY_CHOICE_STORAGE_KEY]);

    return () => window.removeEventListener('resize', handleResize);
  }, [theme]);

  // Main Firebase login function
  const doFirebaseLogin = useCallback(async (firebaseUser, mfaToken, extraData) => {
    try {
      const idToken = await firebaseUser.getIdToken();
      const marketingAttribution = readMarketingAttribution();

      const response = await API.post(GetUserFirebaseSigninPathForApi(), {
        idToken,
        browserFingerprint: _browserFingerprint,
        isMobile: IsMobile(),
        mfaToken: mfaToken || undefined,
        marketingAttribution: marketingAttribution || undefined,
        referrer: marketingAttribution?.referrer || undefined,
        ...extraData,
      }, {
        withCredentials: true,
      });

      if (response.status === 200) {
        pushMarketingEvent({
          event: response.data?.IsNewUser ? 'sign_up' : 'login',
          method: firebaseAuthMethod(firebaseUser),
        });
        if (marketingAttribution) {
          clearMarketingAttribution();
        }
        setRedirectToReferrer(true);
      }
    } catch (err) {
      // Check for server-side MFA verification required (custom token sign-ins)
      if (err.response?.status === 403 && err.response?.data?.error === 'mfa_verification_required') {
        setPendingFirebaseUser(firebaseUser);
        setShowTOTPDialog(true);
        setShowProgressIndicator(false);
        return;
      }
      handleApiError(err);
    }
  }, [handleApiError]);

  // MFA challenge handler
  const handleMFARequired = useCallback((error, attemptedEmail) => {
    setShowProgressIndicator(false);
    const resolver = getMultiFactorResolver(auth, error);
    setMfaResolver(resolver);
    setMfaEmail(attemptedEmail || '');
    setShowMFADialog(true);
  }, []);

  // Link pending credential after successful sign-in
  const linkPendingCredentialIfNeeded = useCallback(async (user) => {
    if (pendingCredential) {
      try {
        await linkWithCredential(user, pendingCredential);
        setPendingCredential(null);
      } catch (linkError) {
        console.error('Account linking failed:', linkError);
      }
    }
    if (accountLinkingContext) {
      try {
        const credential = EmailAuthProvider.credential(
          accountLinkingContext.email,
          accountLinkingContext.password
        );
        await linkWithCredential(user, credential);
        setAccountLinkingContext(null);
      } catch (linkError) {
        console.error('Password linking failed:', linkError);
      }
    }
  }, [pendingCredential, accountLinkingContext]);

  const handleMFASuccess = useCallback(async (userCredential) => {
    setShowMFADialog(false);
    setMfaResolver(null);
    setMfaEmail('');
    setShowProgressIndicator(true);
    await linkPendingCredentialIfNeeded(userCredential.user);
    await doFirebaseLogin(userCredential.user);
  }, [doFirebaseLogin, linkPendingCredentialIfNeeded]);

  const handleMFACancel = useCallback(() => {
    setShowMFADialog(false);
    setMfaResolver(null);
    setMfaEmail('');
  }, []);

  // Server-side TOTP verification (for Intuit/custom token sign-ins)
  const handleTOTPVerify = useCallback(async () => {
    if (totpCode.length !== 6) {
      setTotpError('Please enter a 6-digit code.');
      return;
    }

    setTotpLoading(true);
    setTotpError('');

    try {
      const idToken = await pendingFirebaseUser.getIdToken();
      const response = await API.post(GetUserMFAVerifyTOTPPathForApi(), {
        idToken,
        totpCode,
      });

      setShowTOTPDialog(false);
      setTotpCode('');
      setShowProgressIndicator(true);
      await doFirebaseLogin(pendingFirebaseUser, response.data.verificationToken);
      setPendingFirebaseUser(null);
    } catch (err) {
      console.error('TOTP verification error:', err);
      if (err.response?.status === 401) {
        setTotpError('Invalid code. Please check your authenticator app and try again.');
      } else {
        setTotpError('Verification failed. Please try again.');
      }
    } finally {
      setTotpLoading(false);
    }
  }, [totpCode, pendingFirebaseUser, doFirebaseLogin]);

  const handleTOTPCancel = useCallback(() => {
    setShowTOTPDialog(false);
    setTotpCode('');
    setTotpError('');
    setPendingFirebaseUser(null);
    setTotpShowRecovery(false);
    setTotpRecoveryCode('');
    setTotpRecoverySuccess(false);
  }, []);

  const handleTOTPRecoveryVerify = useCallback(async () => {
    if (!totpRecoveryCode.trim()) {
      setTotpError('Please enter a recovery code.');
      return;
    }

    setTotpRecoveryLoading(true);
    setTotpError('');

    try {
      await API.post(GetUserMFAResetRecoveryPathForApi(), {
        email: pendingFirebaseUser?.email || '',
        recoveryCode: totpRecoveryCode.trim(),
      });
      setTotpRecoverySuccess(true);
    } catch (err) {
      console.error('Error verifying recovery code:', err);
      if (err.response?.status === 400) {
        setTotpError('Invalid recovery code. Please try again.');
      } else {
        setTotpError('Something went wrong. Please try again.');
      }
    } finally {
      setTotpRecoveryLoading(false);
    }
  }, [totpRecoveryCode, pendingFirebaseUser]);

  const handleForgotPassword = useCallback(async () => {
    if (!email) return;
    setForgotPasswordLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setForgotPasswordSent(true);
    } catch (err) {
      console.error('Error sending password reset email:', err);
      showGeneralDialog("Error", "Failed to send password reset email. Please check the email address and try again.");
    } finally {
      setForgotPasswordLoading(false);
    }
  }, [email, showGeneralDialog]);

  // Email+Password: handle submit button
  const handleEmailPasswordSubmit = useCallback(async () => {
    if (!email) return;
    if (!ValidateEmail(email)) {
      showGeneralDialog("E-mail login", "Please enter a valid e-mail address.");
      return;
    }

    setShowProgressIndicator(true);

    // Check migration status first
    try {
      const response = await API.post(GetUserMigrationCheckPathForApi(), { email });
      const status = response.data;

      setShowProgressIndicator(false);

      if (status.NeedsMigration && !status.HasFirebasePassword) {
        setShowMigrationDialog(true);
        return;
      }
    } catch (err) {
      // Non-critical, continue to password dialog
      console.error('Migration check failed:', err);
    }

    setShowProgressIndicator(false);
    setShowPasswordDialog(true);
  }, [email, showGeneralDialog]);

  // Email+Password: handle password auth
  const handlePasswordAuth = useCallback(async () => {
    if (!password) {
      showGeneralDialog("Password Required", "Please enter a password.");
      return;
    }

    setShowProgressIndicator(true);
    setShowPasswordDialog(false);

    try {
      let userCredential;

      if (isLogin) {
        userCredential = await signInWithEmailAndPassword(auth, email, password);
      } else {
        try {
          userCredential = await createUserWithEmailAndPassword(auth, email, password);
        } catch (error) {
          if (error.code === 'auth/email-already-in-use') {
            setAccountLinkingContext({ email, password });
            showGeneralDialog(
              "Account Exists",
              "An account with this email already exists. Please sign in with your existing method (Google, Apple, or Intuit) and your password will be linked automatically."
            );
            setShowProgressIndicator(false);
            return;
          }
          throw error;
        }
      }

      await linkPendingCredentialIfNeeded(userCredential.user);
      await doFirebaseLogin(userCredential.user);
    } catch (error) {
      setShowProgressIndicator(false);
      setPassword('');

      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, email);
      } else if (error.code === 'auth/user-not-found') {
        showGeneralDialog("No Account", "No account exists with this email. Please create an account first.");
      } else if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        showGeneralDialog("Incorrect Password", "The password you entered is incorrect. Please try again.");
      } else {
        handleApiError(error);
      }
    }
  }, [email, password, isLogin, doFirebaseLogin, handleApiError, handleMFARequired, showGeneralDialog, linkPendingCredentialIfNeeded]);

  // Google Sign-In (via Google Identity Services button -> Firebase credential)
  const handleGoogleLoginResponse = useCallback(async (resp) => {
    if (!resp || !resp.credential) return;

    setShowProgressIndicator(true);

    try {
      const credential = GoogleAuthProvider.credential(resp.credential);
      const result = await signInWithCredential(auth, credential);
      await linkPendingCredentialIfNeeded(result.user);
      await doFirebaseLogin(result.user);
    } catch (error) {
      setShowProgressIndicator(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExistsError(error, 'Google');
      } else {
        handleApiError(error);
      }
    }
  }, [doFirebaseLogin, handleApiError, handleAccountExistsError, handleMFARequired, linkPendingCredentialIfNeeded]);

  // Apple Sign-In (via Firebase popup)
  const handleAppleSignIn = useCallback(async () => {
    setShowProgressIndicator(true);

    try {
      const result = await signInWithPopup(auth, appleProvider);
      await linkPendingCredentialIfNeeded(result.user);
      await doFirebaseLogin(result.user);
    } catch (error) {
      setShowProgressIndicator(false);
      if (error.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(error, error.customData?.email);
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        handleAccountExistsError(error, 'Apple');
      } else if (error.code === 'auth/popup-closed-by-user') {
        // User closed the popup, do nothing
      } else {
        handleApiError(error);
      }
    }
  }, [doFirebaseLogin, handleApiError, handleAccountExistsError, handleMFARequired, linkPendingCredentialIfNeeded]);

  // Migration Flow
  const handleStartMigration = useCallback(async () => {
    setShowProgressIndicator(true);

    try {
      await API.post(GetUserEmailLoginOTPSendPathForApi(), {
        UserProvider: 'email',
        Email: email,
        AuthType: 'email',
        BrowserFingerprint: _browserFingerprint,
        IsMobile: IsMobile(),
      });

      setShowMigrationDialog(false);
      setShowOTPDialog(true);
      setShowProgressIndicator(false);
    } catch (err) {
      setShowProgressIndicator(false);
      handleApiError(err);
    }
  }, [email, handleApiError]);

  const handleVerifyOTPAndEstablishPassword = useCallback(async () => {
    if (migrationOTP.length !== 6) {
      showGeneralDialog("Invalid Code", "Please enter a 6-digit code.");
      return;
    }

    if (!migrationPassword || migrationPassword.length < 6) {
      showGeneralDialog("Weak Password", "Password must be at least 6 characters.");
      return;
    }

    if (migrationPassword !== migrationPasswordConfirm) {
      showGeneralDialog("Password Mismatch", "Passwords do not match.");
      return;
    }

    setShowProgressIndicator(true);

    try {
      await API.post(GetUserEstablishPasswordPathForApi(), {
        email,
        otp: migrationOTP,
        password: migrationPassword,
      });

      const userCredential = await signInWithEmailAndPassword(auth, email, migrationPassword);
      await doFirebaseLogin(userCredential.user);
    } catch (err) {
      setShowProgressIndicator(false);
      if (err.code === 'auth/multi-factor-auth-required') {
        handleMFARequired(err, email);
      } else {
        handleApiError(err);
      }
    }
  }, [email, migrationOTP, migrationPassword, migrationPasswordConfirm, doFirebaseLogin, handleApiError, handleMFARequired, showGeneralDialog]);

  const handleCloseMigrationDialog = useCallback(() => {
    setShowMigrationDialog(false);
  }, []);

  // Handle direct login link (UniqueID + OTP in URL)
  useEffect(() => {
    if (match && match.params) {
      const UniqueID = match.params.uniqueID;
      const OTP = match.params.OTP;
      if (UniqueID && OTP) {
        // Legacy direct login links — these still work during migration period
        // via the old /user/signin endpoint. After migration is complete,
        // direct links can be removed.
        const signInPackage = {
          UserProvider: "email",
          AuthType: "emailLink",
          UniqueID,
          OTP,
          BrowserFingerprint: _browserFingerprint,
          IsMobile: IsMobile(),
        };

        API.post('/user/signin', signInPackage)
          .then(resp => {
            if (resp.status === 200) {
              setRedirectToReferrer(true);
            }
          })
          .catch(err => {
            err.closeCallback = () => window.location.href = GetSignInPath();
            handleApiError(err);
          });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect if logged in
  if (redirectToReferrer === true && from) {
    return <Redirect to={from} />;
  }

  // Only show login panel if not direct login
  const isDirectLink = (match && match.params && match.params.uniqueID && match.params.OTP);
  let signInPaper = (<div />);
  let gridLinks = null;

  if (!isDirectLink) {
    const newAccountGridItem = (SignInRoute === "new") ? (
      <Grid item style={{ marginBottom: theme.spacing(2) }}>
        <div style={{
          fontSize:19,
          fontWeight:500,
          color:theme.palette.primary.main,
        }}>
          Create your FREE account
        </div>
      </Grid>
    ) : null;

    const signInGridItem = (SignInRoute === "new") ? (
      <Grid item xs={12} style={{marginTop:theme.spacing(1)}}>
        <Link href="/signin/user">
          <span style={{textDecoration:"underline"}}>Sign in</span>
        </Link>
      </Grid>
    ) : null;

    const intuitSignInGridItem = (!nativeAppMode) ? (
      <Grid item style={{display:"flex",justifyContent:"center"}}>
        <div className={classes.intuitSignInButton}
          onClick={() => HandleSignIn(from)}
        />
      </Grid>
    ) : null;

    const signInGrid = (
      <Grid container
        direction="column"
        spacing={ButtonGridSpacing}
        ref={buttonGridRef}
        className={classes.gridControls}>

        {newAccountGridItem}

        <Grid item>
          <Grid container spacing={2} style={{alignItems:"center"}}>
            <Grid item xs={12}>
              <TextField
                size="small"
                label="Email"
                variant="outlined"
                fullWidth
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => { if (e.keyCode === 13) handleEmailPasswordSubmit(); }}
                value={email}
              />
            </Grid>

            <Grid item xs={12}>
              <Button variant="contained" color="primary"
                style={{
                  height:40,
                  backgroundColor: (SignInRoute === "new") ? green[500] : undefined,
                  color:(SignInRoute === "new") ? "white" : undefined,
                }}
                className={classes.button}
                fullWidth
                onClick={handleEmailPasswordSubmit}>
                <span style={{whiteSpace:"nowrap"}}>{(SignInRoute === "new") ? "Create Account" : "Sign in"}</span>
              </Button>
            </Grid>
          </Grid>
        </Grid>

        <Grid item className={classes.or}>
          <Grid container spacing={0} style={{alignItems:"center"}}>
            <Grid item xs={5}>
              <Divider className={classes.divider} />
            </Grid>
            <Grid item xs={2}>
              or
            </Grid>
            <Grid item xs={5}>
              <Divider className={classes.divider} />
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <GoogleLogin
            text={(SignInRoute === "new") ? "continue_with" : undefined}
            onSuccess={handleGoogleLoginResponse}
            onError={() => {
              console.log('Sign-in Failed');
            }}
            width={googleButtonWidth}
            type="standard"
            logo_alignment="center"
            size="large"
          />
        </Grid>

        <Grid item>
          <Button
            variant="outlined"
            size="large"
            style={{
              backgroundColor:"#fff",
              height:38,
              borderRadius:4,
              borderColor:"#ddd",
            }}
            className={classes.button}
            fullWidth
            onClick={handleAppleSignIn}
          >
            <svg style={{marginLeft:-6,marginRight:10}} aria-hidden="true" width="24" height="24" viewBox="2 12 20 20">
              <g>
                <path d="M12.2337427,16.9879688 C12.8896607,16.9879688 13.7118677,16.5445313 14.2014966,15.9532812 C14.6449341,15.4174609 14.968274,14.6691602 14.968274,13.9208594 C14.968274,13.8192383 14.9590357,13.7176172 14.9405591,13.6344727 C14.2107349,13.6621875 13.3330982,14.1241016 12.8065162,14.7430664 C12.3907935,15.2142188 12.012024,15.9532812 12.012024,16.7108203 C12.012024,16.8216797 12.0305005,16.9325391 12.0397388,16.9694922 C12.0859302,16.9787305 12.1598365,16.9879688 12.2337427,16.9879688 Z M9.92417241,28.1662891 C10.8202857,28.1662891 11.2175318,27.5658008 12.3353638,27.5658008 C13.4716724,27.5658008 13.721106,28.1478125 14.7188404,28.1478125 C15.6980982,28.1478125 16.3540162,27.2424609 16.972981,26.3555859 C17.6658521,25.339375 17.9522388,24.3416406 17.9707154,24.2954492 C17.9060474,24.2769727 16.0306763,23.5101953 16.0306763,21.3576758 C16.0306763,19.491543 17.5088013,18.6508594 17.5919459,18.5861914 C16.612688,17.1819727 15.1253248,17.1450195 14.7188404,17.1450195 C13.6194849,17.1450195 12.7233716,17.8101758 12.1598365,17.8101758 C11.5501099,17.8101758 10.7463794,17.1819727 9.79483648,17.1819727 C7.98413335,17.1819727 6.14571538,18.6785742 6.14571538,21.5054883 C6.14571538,23.2607617 6.8293482,25.1176563 7.67003179,26.3186328 C8.39061773,27.3348438 9.01882085,28.1662891 9.92417241,28.1662891 Z" fill="#000"></path>
              </g>
            </svg>
            <span style={{
              fontWeight:400,
              color:"#222",
              fontSize:14,
            }}>
              {(SignInRoute === "new") ? "Continue with Apple" : "Sign in with Apple"}
            </span>
          </Button>
        </Grid>

        {intuitSignInGridItem}
        {signInGridItem}
      </Grid>
    );

    signInPaper = (
      <Paper elevation={theme.spacing(2)} className={classes.paper}>
        <div style={{textAlign:"center"}}>
          <img className={classes.logo} src="/apcentral.png" alt="AP Central" />
        </div>

        {signInGrid}
      </Paper>
    );

    gridLinks = (
      <Grid container spacing={2} className={classes.gridLinks}>
        <Grid item>
          <Link href="/eula" target="n1legal" color="inherit" variant="body2">
            EULA
          </Link>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="inherit">
            &bull;
          </Typography>
        </Grid>
        <Grid item>
          <Link href={PRIVACY_POLICY_URL} target="n1legal" rel="noopener noreferrer" color="inherit" variant="body2">
            Privacy Policy
          </Link>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="inherit">
            &bull;
          </Typography>
        </Grid>
        <Grid item>
          <Link href="/licenses" target="n1legal" color="inherit" variant="body2">
            Licenses
          </Link>
        </Grid>
        <Grid item>
          <Typography variant="body2" color="inherit">
            &bull;
          </Typography>
        </Grid>
        <Grid item>
          <Link component="button" type="button" onClick={() => resetPrivacyChoice()} color="inherit" variant="body2">
            Cookie choices
          </Link>
        </Grid>
      </Grid>
    );
  }

  return (
    <div className={classes.root}>
      {/* Password Dialog */}
      <Dialog open={showPasswordDialog} onClose={() => { setShowPasswordDialog(false); setPassword(''); setForgotPasswordSent(false); }} maxWidth="xs" fullWidth>
        <DialogTitle>{isLogin ? 'Enter Password' : 'Create Password'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordAuth();
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus
          />
          {isLogin && (
            <div style={{ marginTop: 12 }}>
              {forgotPasswordSent ? (
                <Typography variant="body2" color="primary">
                  Password reset email sent to {email}. Please check your inbox.
                </Typography>
              ) : (
                <Link
                  component="button"
                  variant="body2"
                  onClick={handleForgotPassword}
                  disabled={forgotPasswordLoading}
                >
                  {forgotPasswordLoading ? 'Sending...' : 'Forgot password?'}
                </Link>
              )}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setShowPasswordDialog(false); setPassword(''); setForgotPasswordSent(false); }}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handlePasswordAuth}>
            {isLogin ? 'Sign In' : 'Sign Up'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Migration Dialog */}
      <Dialog open={showMigrationDialog} onClose={handleCloseMigrationDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Establish Password for Enhanced Security</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            We've upgraded to password-based authentication. To continue:
          </Typography>
          <Typography variant="body2" component="div" style={{ paddingLeft: 16 }}>
            <ol style={{ margin: 0 }}>
              <li>We'll send a verification code to {maskEmail(email)}</li>
              <li>Enter the code to verify it's you</li>
              <li>Create a secure password</li>
            </ol>
          </Typography>
          <Typography variant="body2" style={{ marginTop: 16 }}>
            After this one-time setup, you'll sign in with your email and password.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMigrationDialog}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleStartMigration}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* OTP and Password Establishment Dialog */}
      <Dialog open={showOTPDialog} onClose={() => setShowOTPDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Verify Email and Create Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Enter the 6-digit code sent to {maskEmail(email)}
          </Typography>
          <TextField
            fullWidth
            label="Verification Code"
            value={migrationOTP}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setMigrationOTP(val);
            }}
            placeholder="000000"
            inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
            style={{ marginBottom: 16 }}
          />
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={migrationPassword}
            onChange={(e) => setMigrationPassword(e.target.value)}
            style={{ marginBottom: 16 }}
          />
          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={migrationPasswordConfirm}
            onChange={(e) => setMigrationPasswordConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowOTPDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleVerifyOTPAndEstablishPassword}
            disabled={migrationOTP.length !== 6 || !migrationPassword || migrationPassword !== migrationPasswordConfirm}
          >
            Verify & Create Password
          </Button>
        </DialogActions>
      </Dialog>

      {/* MFA Challenge Dialog (Firebase resolver-based) */}
      <MFAChallengeDialog
        open={showMFADialog}
        resolver={mfaResolver}
        email={mfaEmail}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />

      {/* Server-side TOTP Verification Dialog (for Intuit/custom token sign-ins) */}
      <Dialog open={showTOTPDialog} onClose={handleTOTPCancel} maxWidth="xs" fullWidth>
        {totpRecoverySuccess ? (
          <>
            <DialogTitle>MFA Reset Complete</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="primary" style={{ marginBottom: 16 }}>
                Multi-factor authentication has been reset successfully.
              </Typography>
              <Typography variant="body2">
                Please sign in again. You will no longer be prompted for an authenticator code.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" color="primary" onClick={handleTOTPCancel}>
                OK
              </Button>
            </DialogActions>
          </>
        ) : totpShowRecovery ? (
          <>
            <DialogTitle>Use Recovery Code</DialogTitle>
            <DialogContent>
              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter one of the recovery codes you saved when you set up MFA.
              </Typography>

              <TextField
                fullWidth
                label="Recovery code"
                value={totpRecoveryCode}
                onChange={(e) => setTotpRecoveryCode(e.target.value)}
                placeholder="xxxx-xxxx"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpRecoveryCode.trim()) {
                    handleTOTPRecoveryVerify();
                  }
                }}
                style={{ marginBottom: 8 }}
              />

              <Typography variant="body2" color="textSecondary">
                After resetting, you can sign in without an authenticator code and optionally re-enroll.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setTotpShowRecovery(false); setTotpRecoveryCode(''); setTotpError(''); }} disabled={totpRecoveryLoading}>
                Back
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleTOTPRecoveryVerify}
                disabled={totpRecoveryLoading || !totpRecoveryCode.trim()}
              >
                {totpRecoveryLoading ? 'Resetting...' : 'Reset MFA'}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>Multi-Factor Authentication</DialogTitle>
            <DialogContent>
              <Typography variant="body2" style={{ marginBottom: 16 }}>
                Enter the 6-digit code from your authenticator app.
              </Typography>

              {totpError && (
                <Typography variant="body2" color="error" style={{ marginBottom: 16 }}>
                  {totpError}
                </Typography>
              )}

              <TextField
                fullWidth
                value={totpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setTotpCode(val);
                }}
                placeholder="000000"
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' } }}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && totpCode.length === 6) {
                    handleTOTPVerify();
                  }
                }}
                style={{ marginBottom: 16 }}
              />

              <Link
                component="button"
                variant="body2"
                onClick={() => { setTotpShowRecovery(true); setTotpError(''); }}
                style={{ display: 'block', textAlign: 'center' }}
              >
                Can't access your authenticator?
              </Link>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleTOTPCancel} disabled={totpLoading}>Cancel</Button>
              <Button
                onClick={handleTOTPVerify}
                variant="contained"
                color="primary"
                disabled={totpLoading || totpCode.length !== 6}
              >
                {totpLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* General Dialog */}
      <MultiUseDialog Details={generalDialogDetails} />

      {/* Progress Indicator */}
      {showProgressIndicator && <ProgressIndicator showImmediately />}

      <div className={classes.container}>
        {signInPaper}
        {gridLinks}
      </div>
    </div>
  );
};

export default withCookies(withStyles(styles, {withTheme: true})(Login));
